import { lazy, Suspense, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Show, SignIn, SignUp, useAuth } from "@clerk/react";
import { Splash } from "@/components/Splash";

const lazyRetry = (load) => lazy(() => load().catch(() => {
  if (!sessionStorage.getItem("fz_chunk_reload")) {
    sessionStorage.setItem("fz_chunk_reload", "1");
    window.location.reload();
  }
  return load();
}));
import { Toaster } from "@/components/ui/sonner";
import ClerkAuthProvider from "@/components/auth/ClerkAuthProvider";
import { ProfileProvider } from "@/lib/api";
import AppShell from "@/components/AppShell";
const Landing = lazyRetry(() => import("@/pages/Landing"));
const Home = lazyRetry(() => import("@/pages/Home"));
const Matches = lazyRetry(() => import("@/pages/Matches"));
const MatchDetail = lazyRetry(() => import("@/pages/MatchDetail"));
const Terrains = lazyRetry(() => import("@/pages/Terrains"));
const TerrainDetail = lazyRetry(() => import("@/pages/TerrainDetail"));
const ProScores = lazyRetry(() => import("@/pages/ProScores"));
const ProMatch = lazyRetry(() => import("@/pages/ProMatch"));
const Messages = lazyRetry(() => import("@/pages/Messages"));
const Profile = lazyRetry(() => import("@/pages/Profile"));
const PaymentResult = lazyRetry(() => import("@/pages/PaymentResult"));
const AuthLayout = lazyRetry(() => import("@/pages/AuthLayout"));
const Leagues = lazyRetry(() => import("@/pages/Leagues"));
const LeagueDetail = lazyRetry(() => import("@/pages/LeagueDetail"));
const Leaderboard = lazyRetry(() => import("@/pages/Leaderboard"));
const Partner = lazyRetry(() => import("@/pages/Partner"));
const Admin = lazyRetry(() => import("@/pages/Admin"));

const ScrollTop = () => {
  const { pathname } = useLocation();
  useEffect(() => window.scrollTo(0, 0), [pathname]);
  return null;
};

const signedOutHint = () => {
  const m = /(?:^|; )__client_uat(?:_[^=]*)?=([^;]*)/.exec(document.cookie);
  return !m || m[1] === "0";
};

const Protected = () => {
  const { isLoaded } = useAuth();
  if (!isLoaded && signedOutHint()) return <Navigate to="/bienvenue" replace />;
  if (!isLoaded) return <Splash />;
  return (
    <>
      <Show when="signed-in"><ProfileProvider><AppShell /></ProfileProvider></Show>
      <Show when="signed-out"><Navigate to="/bienvenue" replace /></Show>
    </>
  );
};

const RootGate = () => (
  <>
    <Show when="signed-out"><Navigate to="/bienvenue" replace /></Show>
    <Show when="signed-in"><Home /></Show>
  </>
);

function App() {
  useEffect(() => { const t = setTimeout(() => sessionStorage.removeItem("fz_chunk_reload"), 10000); return () => clearTimeout(t); }, []);
  return (
    <BrowserRouter>
      <ClerkAuthProvider>
        <ScrollTop />
        <Suspense fallback={<Splash />}>
        <Routes>
          <Route path="/bienvenue" element={<><Show when="signed-in"><Navigate to="/" replace /></Show><Landing /></>} />
          <Route path="/sign-in/*" element={<AuthLayout><SignIn routing="path" path="/sign-in" signUpUrl="/sign-up" withSignUp /></AuthLayout>} />
          <Route path="/sign-up/*" element={<AuthLayout><SignUp routing="path" path="/sign-up" signInUrl="/sign-in" /></AuthLayout>} />
          <Route element={<Protected />}>
            <Route index element={<RootGate />} />
            <Route path="/matchs" element={<Matches />} />
            <Route path="/matchs/:id" element={<MatchDetail />} />
            <Route path="/terrains" element={<Terrains />} />
            <Route path="/terrains/:id" element={<TerrainDetail />} />
            <Route path="/direct" element={<ProScores />} />
            <Route path="/direct/:id" element={<ProMatch />} />
            <Route path="/championnats" element={<Leagues />} />
            <Route path="/championnats/:id" element={<LeagueDetail />} />
            <Route path="/messages" element={<Messages />} />
            <Route path="/classement" element={<Leaderboard />} />
            <Route path="/partenaire" element={<Partner />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/profil" element={<Profile />} />
            <Route path="/payment/success" element={<PaymentResult success />} />
            <Route path="/payment/cancel" element={<PaymentResult />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
        <Toaster position="top-center" theme="dark" richColors />
      </ClerkAuthProvider>
    </BrowserRouter>
  );
}

export default App;
